import React, { useCallback, useEffect, useId, useState } from "react";
import { Drawer, Popover } from "antd";
import { HiOutlineCheck } from "react-icons/hi2";
import PassengerSupplementPicker from "./PassengerSupplementPicker";
import { FlightExtraTypeIcon } from "./FlightExtraTypeIcon";
import "./ExtrasInteractive.css";

const MOBILE_MAX = 600;

function isIlsDisplayCurrency(currency) {
  const raw = String(currency ?? "").trim();
  if (raw === "₪") return true;
  const u = raw.toUpperCase();
  return u === "ILS" || u === "NIS";
}

function formatPlusUnitPrice(row, options = {}) {
  const ceilIls = options.ceilIlsDisplay === true;
  const amt = row?.fareAmount;
  const curRaw = String(row?.currency || "").trim();
  const c = curRaw.toUpperCase();
  if (Number.isFinite(amt) && amt > 0) {
    let r;
    if (ceilIls && isIlsDisplayCurrency(row.currency)) {
      r = Math.ceil(amt);
    } else {
      r = Math.abs(amt - Math.round(amt)) < 0.01 ? Math.round(amt) : amt;
    }
    const num = String(r);
    if (c === "USD" || c === "$" || c === "") {
      return `+${num}$`;
    }
    return `+${num} ${row.currency}`.trim();
  }
  if (row?.fare) {
    return row.currency ? `+${row.fare} ${row.currency}` : `+${row.fare}`;
  }
  return "";
}

function formatSecondaryPlusPrice(row) {
  const secondary = {
    fareAmount: row?.secondaryFareAmount,
    currency: row?.secondaryCurrency,
  };
  const text = formatPlusUnitPrice(secondary, { ceilIlsDisplay: false });
  if (!text) return "";
  const primaryCur = String(row?.currency ?? "").trim().toUpperCase();
  const secondaryCur = String(row?.secondaryCurrency ?? "").trim().toUpperCase();
  if (primaryCur && secondaryCur && primaryCur === secondaryCur) return "";
  return text;
}

function shortDescription(row) {
  const d = (row.description || "").trim();
  if (d && d !== row.titleLine) {
    return d.length > 80 ? `${d.slice(0, 78)}…` : d;
  }
  return row.type === "TROLLEY"
    ? "טרולי (כבודת יד) — מתאים למדפי המטוס מעל המושב"
    : "כבודה רשומה במטען — לכל קטעי הטיסה בכרטיס";
}

function useNarrowViewport() {
  const [narrow, setNarrow] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(`(max-width: ${MOBILE_MAX}px)`).matches
      : false
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_MAX}px)`);
    const fn = () => setNarrow(mq.matches);
    fn();
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);
  return narrow;
}

export default function ExtraSupplementItem({
  row,
  eligiblePaxList,
  selectedIds,
  onTogglePax,
  idBase,
  ceilDisplayedUnitPrice = false,
}) {
  const narrow = useNarrowViewport();
  const [open, setOpen] = useState(false);
  const reactId = useId();
  const pickerId = `${idBase}-picker-${reactId}`;
  const multiPax = eligiblePaxList.length > 1;

  const close = useCallback(() => setOpen(false), []);

  const toggle = useCallback(
    (paxId, checked) => {
      onTogglePax(row.supplementKey, paxId, checked);
    },
    [onTogglePax, row.supplementKey]
  );

  const count = selectedIds.length;
  const paxById = new Map(eligiblePaxList.map((p) => [p.id, p]));
  const selected = count > 0;
  const plusPrice = formatPlusUnitPrice(row, {
    ceilIlsDisplay: ceilDisplayedUnitPrice,
  });
  const secondaryPlusPrice = formatSecondaryPlusPrice(row);
  const desc = shortDescription(row);

  const picker = (
    <PassengerSupplementPicker
      paxList={eligiblePaxList}
      selectedIds={selectedIds}
      onToggle={toggle}
      onRequestClose={close}
    />
  );

  const activateCard = useCallback(() => {
    if (!eligiblePaxList.length) return;
    if (multiPax) {
      if (narrow) setOpen(true);
      return;
    }
    const onlyId = eligiblePaxList[0]?.id;
    if (!onlyId) return;
    toggle(onlyId, !selectedIds.includes(onlyId));
  }, [
    eligiblePaxList,
    multiPax,
    narrow,
    selectedIds,
    toggle,
  ]);

  const onCardClick = useCallback(
    (e) => {
      if (multiPax && !narrow) {
        return;
      }
      e.preventDefault();
      activateCard();
    },
    [activateCard, multiPax, narrow]
  );

  const onCardKeyDown = useCallback(
    (e) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      if (multiPax && !narrow) return;
      e.preventDefault();
      activateCard();
    },
    [activateCard, multiPax, narrow]
  );

  const ctaLabel = selected ? "נוסף" : "הוסף לטיסה";

  const cardInner = (
    <>
      {selected ? (
        <span className="fb-extra-card__check" aria-hidden>
          <HiOutlineCheck strokeWidth={2.75} />
        </span>
      ) : null}
      <div className="fb-extra-card__body">
        <div
          className={`fb-extra-card__icon-ring fb-extra-card__icon-ring--${row.type === "TROLLEY" ? "trolley" : "bag"}`}
          aria-hidden
        >
          <FlightExtraTypeIcon type={row.type} />
        </div>
        <div className="fb-extra-card__text">
          <div className="fb-extra-card__title">{row.titleLine}</div>
          <p className="fb-extra-card__desc">{desc}</p>
          {row.perPaxDirectionHe ? (
            <p className="fb-extra-card__meta">{row.perPaxDirectionHe}</p>
          ) : null}
        </div>
      </div>
      <div className="fb-extra-card__footer">
        {plusPrice ? (
          <div
            className="fb-extra-card__price"
            dir="rtl"
            translate="no"
            aria-label={`${plusPrice} לנוסע`}
          >
            <span className="fb-extra-card__price-suffix">לנוסע</span>
            <span className="fb-extra-card__price-value" dir="ltr">
              {plusPrice}
            </span>
            {secondaryPlusPrice ? (
              <span className="fb-extra-card__price-secondary" dir="ltr">
                ({secondaryPlusPrice})
              </span>
            ) : null}
          </div>
        ) : (
          <div className="fb-extra-card__price fb-extra-card__price--empty" />
        )}
        <span
          className={
            selected
              ? "fb-extra-card__cta fb-extra-card__cta--added"
              : "fb-extra-card__cta fb-extra-card__cta--pick"
          }
          aria-hidden
        >
          {selected ? (
            <>
              <span className="fb-extra-card__cta-check" aria-hidden>
                <HiOutlineCheck strokeWidth={2.75} />
              </span>
              {ctaLabel}
            </>
          ) : (
            ctaLabel
          )}
        </span>
      </div>
    </>
  );

  const cardClass =
    `fb-extra-card fb-extra-card--upsell${selected ? " fb-extra-card--selected" : ""}`.trim();

  const ariaLabel = selected
    ? `${row.titleLine} — נוסף, לחצו לעריכה`
    : `${row.titleLine} — ${multiPax ? "בחרו נוסעים" : "לחצו להוספה לטיסה"}`;

  const cardNode = multiPax && !narrow ? (
    <Popover
      content={picker}
      title={null}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
      destroyOnHidden
    >
      <div
        className={cardClass}
        role="button"
        tabIndex={0}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={pickerId}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
      >
        {cardInner}
      </div>
    </Popover>
  ) : (
    <div
      className={cardClass}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      aria-expanded={narrow && multiPax ? open : undefined}
      aria-controls={narrow && multiPax ? pickerId : undefined}
      onClick={onCardClick}
      onKeyDown={onCardKeyDown}
    >
      {cardInner}
    </div>
  );

  return (
    <div className="fb-extra-item fb-extra-item--upsell-wrap">
      {cardNode}
      {narrow && multiPax ? (
        <Drawer
          title={row.titleLine}
          placement="bottom"
          height="auto"
          styles={{ body: { paddingBottom: 16 } }}
          open={open}
          onClose={close}
          destroyOnHidden
        >
          <div id={pickerId}>{picker}</div>
        </Drawer>
      ) : null}

      {count > 0 ? (
        <div className="fb-extra-selected fb-extra-selected--compact">
          <span className="fb-extra-selected__inline">
            {selectedIds.map((pid) => {
              const p = paxById.get(pid);
              const label = p?.labelHe ?? pid;
              return (
                <span key={pid} className="fb-extra-chip">
                  {label}
                  <button
                    type="button"
                    className="fb-extra-chip__x"
                    aria-label={`הסר ${label}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggle(pid, false);
                    }}
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </span>
        </div>
      ) : null}
    </div>
  );
}
